import axios from 'axios';

const API_URL = "/api";

// Axios instance with token interceptor
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach token to every request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

// Logout user on unauthenticated request
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      window.location.href = "/auth";
    }
    return Promise.reject(error);
  }
);

// Sign Up User
export const signUpUser = async (fullName, email, password) => {
  const response = await apiClient.post('/signup', { fullName, email, password });
  return response.data;
};

// Sign In User
export const signInUser = async (email, password, rememberMe) => {
  const response = await apiClient.post('/signin', { email, password, rememberMe });
  return response.data;
};

// Forgot Password
export const forgotPassword = async (email) => {
  const response = await apiClient.post('/forgot-password', { email });
  return response.data;
};

// Verify OTP
export const verifyOtp = async (email, otp) => {
  const response = await apiClient.post('/verify-otp', { email, otp });
  return response.data;
};

// Reset Password
export const resetPassword = async (resetToken, newPassword, otp, email) => {
  const response = await apiClient.post('/reset-password', {
    resetToken,
    new_password: newPassword,
    otp,
    email
  });
  return response.data;
};

// Create New Chat
export const createChat = async (user_id, title, model_id = 1) => {
  const response = await apiClient.post('/newchat', { user_id, title, model_id });
  return response.data;
};

// Streaming Query Response
export const fetchAIResponse = async (chatId, userMessage, enableWebSearch = false) => {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const response = await fetch(`${API_URL}/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ chat_id: chatId, user_message: userMessage, enable_web_search: enableWebSearch }),
    });

    if (!response.body) throw new Error('No response body available.');
    return response.body.getReader();
  } catch (error) {
    console.error('Error fetching AI response:', error);
    throw error;
  }
};

// Store message to database
export const storeMessageToDB = async (sender, chat_id, message) => {
    const response = await apiClient.post('/storeMessage', JSON.stringify({ sender, chat_id, message }));
    return response.data;
};

// Fetch user chats
export const fetchUserChats = async (user_id) => {
  const response = await apiClient.get('/chats', { params: { user_id } });
  return response.data;
};

// Fetch messages for a chat
export const fetchChatMessages = async (chat_id) => {
  const response = await apiClient.get('/messages', { params: { chat_id } });
  return response.data;
};

// Fetch Chat Model ID
export const fetchChatModel = async (chat_id) => {
  const response = await apiClient.get(`/chat-model-id/${chat_id}`);
  return response.data;
};

// Set Model ID for Chat
export const updateChatModel = async (chatId, modelId) => {
  const response = await apiClient.put(`/set-chat-model`, { chat_id: chatId, model_id: modelId });
  return response.data;
};

// Rename chat
export const renameChatAPI = async (chatId, newTitle) => {
  const response = await apiClient.put('/renameChat', { chat_id: chatId, new_title: newTitle });
  return response.data;
};

// Delete chat (and its messages)
export const deleteChatAPI = async (chatId) => {
  const response = await apiClient.delete(`/deleteChat/${chatId}`);
  return response.data;
};

// Suggest new chat's name to rename
export const suggestTitle = async (chat_id, query) => {
  return apiClient.post('/suggestTitle', { chat_id, query })
    .then(res => res.data)
    .catch(() => ({ title: 'Untitled Chat' }));
};

// Delete all chats for the user
export const deleteAllChats = async () => {
  const response = await apiClient.post('/delete-all-chats');
  return response.data;
};

// Delete the user account and associated data
export const deleteUserAccount = async () => {
  const response = await apiClient.post('/delete-account');
  return response.data;
};

// Update the user's name
export const updateUserName = async (newName) => {
  const response = await apiClient.post('/update-username', { new_name: newName });
  return response.data;
};

// Retrieve the user's name
export const getUserInfo = async () => {
  const response = await apiClient.get('/get-user-info');
  return response.data;
};

// Change user password
export const changePassword = async (oldPassword, newPassword) => {
  const response = await apiClient.post('/change-password', { oldPassword, newPassword });
  return response.data;
};

// Upload file and extract text
export const uploadFileAPI = async (formData) => {
  const response = await apiClient.post('/upload', formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

// Check if user has remaining tokens for generating responses
export const checkRemainingTokens = async () => {
  const response = await apiClient.get('/has-remaining-tokens');
  return response.data;
};

// Check if user can upload a file
export const checkUploadFile = async () => {
  const response = await apiClient.get('/can-upload-file');
  return response.data;
};

// Check if input tokens are within acceptable range
export const checkInputTokens = async (inputText) => {
  const response = await apiClient.post('/check-input-tokens', { input_text: inputText });
  return response.data;
};

export const storeMessageFeedback = async (messageId, feedback) => {
  const response = await apiClient.post('/store-message-feedback', { message_id: messageId, feedback });
  return response.data;
};

// Admin Panel APIs
export const getAllUsers = async () => {
  const response = await apiClient.get('/get-all-users');
  return response.data;
};

export const adminDeleteUser = async (userId) => {
  const response = await apiClient.post('/admin-delete-user', { user_id: userId });
  return response.data;
};

export const adminEditUser = async (userId, newName) => {
  const response = await apiClient.post('/admin-edit-user', { user_id: userId, new_name: newName });
  return response.data;
};

export const getAllFeedbacks = async () => {
  const response = await apiClient.get('/get-all-feedbacks');
  return response.data;
};

export const getUserCustomization = async () => {
  const response = await apiClient.get('/customization');
  return response.data;
};

export const updateUserCustomization = async (customizationData) => {
  const response = await apiClient.post('/customization/update', customizationData);
  return response.data;
};

export const getModelQueryLogs = async () => {
  const response = await apiClient.get('/model-query-logs');
  return response.data;
};

export const getActivityLogs = async () => {
  const response = await apiClient.get('/activity-logs');
  return response.data;
};
